// ui/src/components/shared/PlanVersionDeltaSummary.jsx

/**
 * @file PlanVersionDeltaSummary.jsx
 * @description Compact summary of field-level deltas between two plan versions.
 *   Renders a collapsible change count badge and expandable section list.
 *   Consumes delta payload from ep 31 (GET /api/v1/plans/{id}/version-compare).
 *   Also used alongside ep 22 (plan approval-review) for CA-2 amendment review.
 * @module shared
 * @author data-fit LLC · DecisionFlow · S775
 * @see api_spec_fileB_v4_final.md §2.3 — version-compare response (ep 31)
 * @migration OI-S774-VERSION-DELTA-PAYLOAD — VersionDeltaPayload shape not yet
 *   formally defined in spec. Wire to router sections[] shape when resolved.
 */

import { useId, useState } from 'react';
import ProvisionalBadge from './ProvisionalBadge';
import DataFreshnessIndicator from './DataFreshnessIndicator';
import PlanVersionCompare from './PlanVersionCompare';

/**
 * @typedef {{
 *   sectionKey: string,
 *   sectionLabel: string,
 *   changes: Array<{
 *     fieldKey: string,
 *     fieldLabel: string,
 *     before: string | null,
 *     after: string,
 *     changeType: 'modified' | 'added' | 'removed'
 *   }>
 * }} DeltaSection
 */

/**
 * @typedef {{
 *   before: number | null,
 *   after: number | null,
 *   delta: number | null,
 *   currencyCode: string,
 *   freshnessTimestamp: string | null,
 *   isProvisional: boolean,
 *   calculationRunReference?: string | null
 * }} ProjectedImpact
 */

/**
 * @param {{
 *   baseVersionLabel: string,
 *   compareVersionLabel: string,
 *   economicEffectiveDate: string | null,
 *   approvalDate: string | null,
 *   totalChanges: number,
 *   affectedPositions: number | null,
 *   projectedImpact: ProjectedImpact | null,
 *   delta: DeltaSection[],
 *   splitPeriodNote: string | null,
 *   mode: 'approval' | 'observer' | 'finance',
 *   unchangedSections: string[],
 *   className?: string
 * }} props
 */
export default function PlanVersionDeltaSummary({
  baseVersionLabel = 'Current',
  compareVersionLabel = 'Proposed',
  economicEffectiveDate = null,
  approvalDate = null,
  totalChanges = 0,
  affectedPositions = null,
  projectedImpact = null,
  delta = [],
  splitPeriodNote = null,
  mode = 'approval',
  unchangedSections = [],
  className = '',
}) {
  const [unchangedOpen, setUnchangedOpen] = useState(false);
  const [approvalComparisonOpen, setApprovalComparisonOpen] = useState(true);
  const [financeChangesOpen, setFinanceChangesOpen] = useState(false);

  const unchangedId = useId();

  const effectiveDateText = formatDate(economicEffectiveDate);
  const approvalDateText = formatDate(approvalDate);

  return (
    <section
      aria-label="Plan version delta summary"
      className={[
        'rounded-[12px]',
        'border border-[rgb(var(--color-border))]',
        'bg-[rgb(var(--color-surface))]',
        className,
      ].join(' ')}
    >
      {mode === 'finance' ? (
        <FinanceHeader
          baseVersionLabel={baseVersionLabel}
          compareVersionLabel={compareVersionLabel}
          economicEffectiveDate={effectiveDateText}
        />
      ) : (
        <header className="border-b border-[rgb(var(--color-border))] p-4">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h2 className="text-[13px] font-semibold text-[rgb(var(--color-text))]">
              {baseVersionLabel} → {compareVersionLabel}
            </h2>

            {effectiveDateText && (
              <span className="text-[13px] font-semibold text-[rgb(var(--color-primary))]">
                · Economic effective date: {effectiveDateText}
              </span>
            )}
          </div>

          {economicEffectiveDate && (
            <p className="mt-1 text-[11px] font-medium text-[rgb(var(--color-text-muted))]">
              Approval date does not determine economic applicability.
            </p>
          )}

          {mode === 'approval' && approvalDateText && (
            <p className="mt-1 text-[11px] text-[rgb(var(--color-text-muted))]">
              Workflow approval date: {approvalDateText}
            </p>
          )}

          {mode === 'observer' && effectiveDateText && (
            <p className="mt-1 text-[12px] text-[rgb(var(--color-text-muted))]">
              These changes apply economically from {effectiveDateText}.
            </p>
          )}

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
            <span className="font-semibold text-[rgb(var(--color-text))]">
              {totalChanges} {totalChanges === 1 ? 'change' : 'changes'}
            </span>

            {mode === 'approval' && affectedPositions !== null && (
              <span className="font-semibold text-[rgb(var(--color-text))]">
                {affectedPositions.toLocaleString('en-US')} positions affected
              </span>
            )}
          </div>
        </header>
      )}

      <div className="space-y-4 p-4">
        <ImpactPanel impact={projectedImpact} mode={mode} />

        {splitPeriodNote && (
          <div
            role="note"
            aria-label={`Split-period event-date resolution: ${splitPeriodNote}`}
            className="modifier-manager-info text-[12px]"
          >
            <strong>Split-period event attribution:</strong> {splitPeriodNote}
          </div>
        )}

        {mode === 'approval' && (
          <PlanVersionCompare
            active={approvalComparisonOpen}
            onToggle={() =>
              setApprovalComparisonOpen((current) => !current)
            }
            baseVersionLabel={baseVersionLabel}
            compareVersionLabel={compareVersionLabel}
            delta={delta}
            effectiveDateNote={splitPeriodNote || undefined}
            totalChanges={totalChanges}
          />
        )}

        {mode === 'observer' && (
          <ObserverChanges delta={delta} />
        )}

        {mode === 'finance' && (
          <PlanVersionCompare
            active={financeChangesOpen}
            onToggle={() => setFinanceChangesOpen((current) => !current)}
            baseVersionLabel={baseVersionLabel}
            compareVersionLabel={compareVersionLabel}
            delta={delta}
            effectiveDateNote={splitPeriodNote || undefined}
            totalChanges={totalChanges}
          />
        )}

        {mode === 'approval' && unchangedSections.length > 0 && (
          <div className="border-t border-[rgb(var(--color-border))] pt-3">
            <button
              type="button"
              className="text-button"
              aria-expanded={unchangedOpen}
              aria-controls={unchangedId}
              onClick={() => setUnchangedOpen((current) => !current)}
            >
              {unchangedOpen ? '▾' : '▸'} Unchanged:{' '}
              {unchangedSections.join(', ')} ({unchangedSections.length})
            </button>

            <div id={unchangedId} hidden={!unchangedOpen} className="mt-2">
              <ul className="flex flex-wrap gap-2" aria-label="Unchanged sections">
                {unchangedSections.map((section) => (
                  <li
                    key={section}
                    className={[
                      'rounded-[999px]',
                      'bg-[rgb(var(--color-surface-muted))]',
                      'px-2 py-1 text-[11px]',
                      'text-[rgb(var(--color-text-muted))]',
                    ].join(' ')}
                  >
                    {section}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function FinanceHeader({
  baseVersionLabel,
  compareVersionLabel,
  economicEffectiveDate,
}) {
  return (
    <header className="border-b border-[rgb(var(--color-border))] p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
        Version attribution
      </p>

      <h2 className="mt-1 text-[13px] font-semibold text-[rgb(var(--color-text))]">
        {baseVersionLabel} → {compareVersionLabel}
      </h2>

      {economicEffectiveDate && (
        <>
          <p className="mt-1 text-[13px] font-semibold text-[rgb(var(--color-primary))]">
            Economic effective date: {economicEffectiveDate}
          </p>
          <p className="mt-1 text-[11px] text-[rgb(var(--color-text-muted))]">
            Version attribution follows economic event date, not workflow approval
            date.
          </p>
        </>
      )}
    </header>
  );
}

function ObserverChanges({ delta }) {
  const changedSections = delta.filter(
    (section) => Array.isArray(section.changes) && section.changes.length > 0,
  );

  if (!changedSections.length) {
    return (
      <p className="text-[12px] text-[rgb(var(--color-text-muted))]">
        No configuration changes detected.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {changedSections.map((section) => (
        <section key={section.sectionKey}>
          <h3 className="text-[12px] font-semibold text-[rgb(var(--color-text))]">
            {section.sectionLabel}
          </h3>

          <ul className="mt-1 space-y-1">
            {section.changes.map((change) => (
              <li
                key={`${section.sectionKey}-${change.fieldKey}`}
                className="text-[12px] text-[rgb(var(--color-text))]"
              >
                <span className="font-medium">{change.fieldLabel}:</span>{' '}
                {change.changeType === 'removed'
                  ? 'Removed'
                  : change.after}
                <span className="ml-1 text-[10px] text-[rgb(var(--color-text-muted))]">
                  ({humanChangeType(change.changeType)})
                </span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function ImpactPanel({ impact, mode }) {
  if (!impact) {
    return (
      <div
        aria-label="Projected economic impact not yet available"
        className="rounded-[8px] bg-[rgb(var(--color-surface-muted))] p-3"
      >
        <p className="text-[12px] font-medium text-[rgb(var(--color-text))]">
          Impact not yet available
        </p>
      </div>
    );
  }

  const {
    before,
    after,
    delta,
    currencyCode,
    freshnessTimestamp,
    isProvisional,
    calculationRunReference,
  } = impact;

  const hasAnyEconomicResult =
    before !== null || after !== null || delta !== null;

  if (!hasAnyEconomicResult) {
    return (
      <div
        aria-label="Projected economic impact not yet available"
        className="rounded-[8px] bg-[rgb(var(--color-surface-muted))] p-3"
      >
        <p className="text-[12px] font-medium text-[rgb(var(--color-text))]">
          Impact not yet available
        </p>
      </div>
    );
  }

  /*
   * DP-4 fail closed:
   * an authoritative figure must be traceable to persisted engine evidence.
   */
  if (!isProvisional && !calculationRunReference) {
    return (
      <div
        role="status"
        aria-label="Authoritative impact cannot be displayed because its calculation evidence reference is unavailable"
        className="modifier-manager-warning text-[12px]"
      >
        Authoritative impact is available, but its persisted calculation
        reference was not supplied. The amount is withheld until the engine
        evidence can be linked.
      </div>
    );
  }

  if (isProvisional && !freshnessTimestamp) {
    return (
      <div
        role="status"
        aria-label="Projected impact cannot be displayed because freshness evidence is unavailable"
        className="modifier-manager-warning text-[12px]"
      >
        Projected impact exists, but freshness evidence is unavailable. The
        amount is withheld until projection freshness is supplied.
      </div>
    );
  }

  const formattedBefore = formatCurrency(before, currencyCode);
  const formattedAfter = formatCurrency(after, currencyCode);
  const formattedDelta = formatSignedCurrency(delta, currencyCode);
  const percentageDelta = computeDisplayPercentage(before, delta);

  let ariaText = isProvisional
    ? 'Provisional projected economic impact.'
    : `Authoritative economic impact from calculation ${calculationRunReference}.`;

  if (formattedBefore !== null) ariaText += ` Before ${formattedBefore}.`;
  if (formattedAfter !== null) ariaText += ` After ${formattedAfter}.`;
  if (formattedDelta !== null) ariaText += ` Delta ${formattedDelta}.`;

  return (
    <div
      aria-label={ariaText}
      className={[
        'rounded-[8px]',
        'border border-[rgb(var(--color-border))]',
        'bg-[rgb(var(--color-bg))]',
        'p-3',
      ].join(' ')}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
        {isProvisional ? 'Projected impact' : 'Authoritative engine impact'}
      </p>

      {mode === 'observer' ? (
        <div className="mt-1">
          <span className="text-[13px] font-semibold text-[rgb(var(--color-text))]">
            Net impact:{' '}
            {formattedDelta === null ? 'Not available' : formattedDelta}
          </span>
          {percentageDelta !== null && (
            <span className="ml-2 text-[12px] text-[rgb(var(--color-text-muted))]">
              ({percentageDelta})
            </span>
          )}
        </div>
      ) : (
        <div className="mt-1 flex flex-wrap items-baseline gap-2 text-[13px]">
          <span className="text-[rgb(var(--color-text-muted))]">
            Before: {formattedBefore ?? 'Not available'}
          </span>

          <span aria-hidden="true">→</span>

          <span className="font-semibold text-[rgb(var(--color-text))]">
            After: {formattedAfter ?? 'Not available'}
          </span>

          {formattedDelta !== null && (
            <span className="font-semibold text-[rgb(var(--color-primary))]">
              · {formattedDelta}
              {percentageDelta !== null ? ` (${percentageDelta})` : ''}
            </span>
          )}
        </div>
      )}

      {isProvisional ? (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <ProvisionalBadge
            freshnessTimestamp={freshnessTimestamp}
            size="sm"
          />
          <DataFreshnessIndicator
            signal="projection"
            freshnessAt={freshnessTimestamp}
            status="current"
          />
        </div>
      ) : (
        <div className="mt-2 text-[11px] text-[rgb(var(--color-text-muted))]">
          Persisted engine calculation:{' '}
          <span className="font-semibold text-[rgb(var(--color-text))]">
            {calculationRunReference}
          </span>
        </div>
      )}
    </div>
  );
}

function formatCurrency(amount, currencyCode = 'USD') {
  if (amount === null || amount === undefined) return null;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatSignedCurrency(amount, currencyCode = 'USD') {
  if (amount === null || amount === undefined) return null;

  const absolute = formatCurrency(Math.abs(amount), currencyCode);

  if (amount > 0) return `+${absolute}`;
  if (amount < 0) return `−${absolute}`;
  return absolute;
}

/*
 * Display-only arithmetic over server-supplied aggregate values.
 * It does not recreate plan economics or payout reasoning.
 *
 * If strict DP-6 interpretation requires even this percentage to be
 * server-authored, add deltaPct to the API payload and render it verbatim.
 */
function computeDisplayPercentage(before, delta) {
  if (
    before === null ||
    before === undefined ||
    delta === null ||
    delta === undefined ||
    before === 0
  ) {
    return null;
  }

  const value = (delta / Math.abs(before)) * 100;
  const prefix = value > 0 ? '+' : '';

  return `${prefix}${value.toFixed(1)}%`;
}

function formatDate(isoDate) {
  if (!isoDate) return null;

  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function humanChangeType(type) {
  return {
    modified: 'modified',
    added: 'added',
    removed: 'removed',
  }[type];
}